import React, { useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Button,
    useTheme,
    ImageList,
    ImageListItem,
    IconButton,
} from '@mui/material';
import { DownloadOutlined, TableChartOutlined, Visibility } from "@mui/icons-material";
import { ContentCopy } from "@mui/icons-material"; // Import the copy icon

import { DataGrid } from "@mui/x-data-grid";
import { useGetExperimentsQuery } from "state/api";
import Header from "components/Header";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import axios from "axios";
let HOST_URL = "https://s1.bioengineai.com";
//let HOST_URL = "http://localhost:9000";

const Experiments = () => {
  const theme = useTheme();

  // values to be sent to the backend
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const { data, isLoading } = useGetExperimentsQuery({
    page,
    pageSize,
    sort: JSON.stringify(sort),
    search,
  });

  const [copiedMessage, setCopiedMessage] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [showHistoryTable, setShowHistoryTable] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [descriptionWidth, setDescriptionWidth] = useState('100%');
  const [displayedImages, setDisplayedImages] = useState({});
  const [startIndex, setStartIndex] = useState(0);

  const rowsPerPage = 10; // Adjust as needed
  const totalPages = Math.ceil(chatHistory.length / rowsPerPage);
  const currentPage = Math.floor(startIndex / rowsPerPage) + 1;

  const columns = [
    {
      field: "experiment_id",
      headerName: "Experiment ID",
      flex: 0.5,
    },
    {
      field: "descriptions",
      headerName: "Descriptions",
      flex: 1,
    },
    {
      field: "h5ad",
      headerName: "h5ad source",
      flex: 1,
      renderCell: (params) => `s3://cytohub-public-content/${params.value}`,
    },
    {
      field: "n_obs",
      headerName: "Total Obs",
      flex: 1,
      renderCell: (params) => `${Number(params.value)}`,
    },
    {
      field: "n_vars",
      headerName: "Total Genes (filtered)",
      flex: 1,
      renderCell: (params) => `${Number(params.value)}`,
    },
    {
      field: "violin_plot",
      headerName: "violinplot_source",
      flex: 1,
      renderCell: (params) => `s3://cytohub-public-content/${params.value}`,
    },
    {
      field: "umap_plot",
      headerName: "umap_source",
      flex: 1,
      renderCell: (params) => `s3://cytohub-public-content/${params.value}`,
    },
    {
      field: "dot_plot",
      headerName: "dotplot_source",
      flex: 1,
      renderCell: (params) => `s3://cytohub-public-content/${params.value}`,
    },
  ];

    // add copy content button
    const handleCopy = (text) => {
      navigator.clipboard.writeText(text); // Use Clipboard API
        setCopiedMessage(text); // Set the copied message for the tooltip
    };

    const fetchChatHistory = async () => {
      try {
        const response = await axios.get(`${HOST_URL}/experiment/getExperimentHistory`);
        setChatHistory(response.data);
        setShowHistoryTable(true);
      } catch (error) {
        console.error("Error fetching experiments: ", error);
      }
    };

    const formatNumberWithCommas = (number) => {
      if (typeof number !== 'number') {
        return number;
      }
      return number.toLocaleString();
    };

    const toggleExpand = (id) => {
      setExpandedRows((prevExpandedRows) => ({
        ...prevExpandedRows,
        [id]: !prevExpandedRows[id],
      }));
      setDescriptionWidth((prevWidth) => (prevWidth === '100%' ? '150%' : '100%'));
    };

    const handleDisplayClick = (experimentId, row) => {
      setDisplayedImages((prevDisplayedImages) => ({
        ...prevDisplayedImages,
        [experimentId]: [
          { url: row.violin_plot, title: 'Violin Plot' },
          { url: row.umap_plot, title: 'UMAP Plot' },
          { url: row.dot_plot, title: 'Dot Plot' },
        ].filter(item => item.url),
      }));
      setExpandedRows((prevExpandedRows) => ({
        ...prevExpandedRows,
        [experimentId]: true, // Optionally expand the row when display is clicked
      }));
    };

    const handleNextRows = () => {
      setStartIndex((prevStart) => Math.min(prevStart + rowsPerPage, chatHistory.length - rowsPerPage));
    };

    const displayedData = chatHistory.slice(startIndex, startIndex + rowsPerPage);

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="EXPERIMENTS" subtitle="Entire list of experiments" />

      <Box>
        <Button
            sx={{
              backgroundColor: theme.palette.secondary.light,
              color: theme.palette.background.alt,
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
              '&:hover': {
                backgroundColor: theme.palette.secondary.light,
                opacity: [0.9, 0.8, 0.7],
              },
            }}
            onClick={fetchChatHistory}
          >
            <TableChartOutlined sx={{ mr: "10px" }} />
            Download Experiment Details
          </Button>
        </Box>

        {showHistoryTable && (
        <TableContainer component={Paper} style={{ marginTop: '20px' }}>
          <Table aria-label="chat history table">
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Experiment ID</TableCell>
                <TableCell>Experiment Date</TableCell>
                <TableCell>Dataset Name</TableCell>
                <TableCell>Transcriptome</TableCell>
                <TableCell>Chemistry</TableCell>
                <TableCell>Estimated Number of Cells</TableCell>
                <TableCell>Observations (n_obs)</TableCell>
                <TableCell>Variables (n_vars)</TableCell>
                <TableCell>Source Dataset</TableCell>
                <TableCell>Processed Dataset URL</TableCell>
                <TableCell>Violin Plot</TableCell>
                <TableCell>UMAP Plot</TableCell>
                <TableCell>Dot Plot</TableCell>
                <TableCell style={{ width: descriptionWidth }}>Descriptions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedData.map((row) => (
                <React.Fragment key={row.experiment_id}>
                  <TableRow>
                    <TableCell>
                      <IconButton onClick={() => handleDisplayClick(row.experiment_id, row)}>
                        <Visibility />
                      </IconButton>
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {row.experiment_id}
                    </TableCell>
                    <TableCell>{row.experiment_date}</TableCell>
                    <TableCell>{row.dataset_name}</TableCell>
                    <TableCell>{row.transcriptome}</TableCell>
                    <TableCell>{row.chemistry}</TableCell>
                    <TableCell>{formatNumberWithCommas(row.est_number_of_cells)}</TableCell>
                    <TableCell>{formatNumberWithCommas(row.n_obs)}</TableCell>
                    <TableCell>{formatNumberWithCommas(row.n_vars)}</TableCell>
                    <TableCell>{row.src_url}</TableCell>
                    <TableCell>{row.h5ad}</TableCell>
                    <TableCell>{row.violin_plot}</TableCell>
                    <TableCell>{row.umap_plot}</TableCell>
                    <TableCell>{row.dot_plot}</TableCell>
                    <TableCell style={{ wordBreak: 'break-word', width: descriptionWidth }}>
                      {row.descriptions.length > 100 && !expandedRows[row.experiment_id]
                        ? `${row.descriptions.substring(0, 100)}...`
                        : row.descriptions}
                      {row.descriptions.length > 100 && (
                        <Button size="small" onClick={() => toggleExpand(row.experiment_id)}>
                          {expandedRows[row.experiment_id] ? 'Less' : 'More'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {expandedRows[row.experiment_id] && displayedImages[row.experiment_id] && displayedImages[row.experiment_id].length > 0 && (
                    <TableRow>
                      <TableCell colSpan={15}> {/* Ensure colSpan matches your number of columns */}
                        <div style={{ marginTop: '10px' }}>
                          <ImageList rowHeight={200} cols={displayedImages[row.experiment_id].length || 1}>
                            {displayedImages[row.experiment_id].map((item, index) => (
                              <ImageListItem key={index}>
                                <img
                                  src={`https://content.cytohub.com/${item.url}`} 
                                  alt={item.title}
                                  loading="lazy"
                                  style={{ height: '100%', width: 'auto', objectFit: 'contain' }} // Added style for better image display
                                />
                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', padding: '8px', fontSize: '0.8em', textAlign: 'center' }}> {/* Adjusted style for title overlay */}
                                  {item.title}
                                </div>
                              </ImageListItem>
                            ))}
                          </ImageList>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>)}

      {chatHistory.length > rowsPerPage && (
        <Button
          onClick={handleNextRows}
          disabled={startIndex + rowsPerPage >= chatHistory.length}
          style={{ marginTop: '10px' }}
        >
          Next {rowsPerPage} Rows
        </Button>
      )}

      <Box
        height="80vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.light,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          loading={isLoading || !data}
          getRowId={(row) => row._id}
          rows={(data && data.experiments) || []}
          columns={columns}
          rowCount={(data && data.total) || 0}
          rowsPerPageOptions={[20, 50, 100]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          sortingMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          onSortModelChange={(newSortModel) => setSort(...newSortModel)}
          components={{ Toolbar: DataGridCustomToolbar }}
          componentsProps={{
            toolbar: { searchInput, setSearchInput, setSearch },
          }}
        />
      </Box>
    </Box>
  );
};

export default Experiments;
